// Hit / miss mark for one KPI in one period (the weekly grid). Never colour alone: each state
// has its own icon shape and a text label (visible or screen-reader only). Status colours are
// the mode-invariant status tokens; "no data" is grey, like `insufficient_data` everywhere.
import {
  CircleAlertIcon,
  CircleCheckIcon,
  CircleDashedIcon,
  CircleXIcon,
  type LucideIcon,
} from "lucide-react";
import type * as React from "react";

import { cn } from "../cn";

/** `near` (TEAM-KPI): amber under the metric's warning bands — short of green, above red. */
export type ScoreMarkState = "hit" | "near" | "miss" | "none";

export const SCORE_MARK_LABELS: Record<ScoreMarkState, string> = {
  hit: "Hit",
  near: "Near target",
  miss: "Missed",
  none: "No data",
};

const ICON: Record<ScoreMarkState, LucideIcon> = {
  hit: CircleCheckIcon,
  near: CircleAlertIcon,
  miss: CircleXIcon,
  none: CircleDashedIcon,
};

const TONE: Record<ScoreMarkState, string> = {
  hit: "text-status-good",
  near: "text-warning-text",
  miss: "text-status-critical",
  none: "text-muted-foreground",
};

export function ScoreMark({
  state,
  label,
  showLabel = false,
  className,
  ...props
}: {
  state: ScoreMarkState;
  /** Replaces the default state label (e.g. "Hit: 42 of 40"). */
  label?: string;
  /** Show the label next to the icon instead of only to screen readers. */
  showLabel?: boolean;
} & Omit<React.ComponentProps<"span">, "children">) {
  const Icon = ICON[state];
  const text = label ?? SCORE_MARK_LABELS[state];
  return (
    <span
      data-slot="score-mark"
      data-state={state}
      className={cn("inline-flex items-center gap-1 text-xs text-foreground", className)}
      {...props}
    >
      <Icon className={cn("size-4 shrink-0", TONE[state])} aria-hidden="true" />
      <span className={showLabel ? undefined : "sr-only"}>{text}</span>
    </span>
  );
}
